import {openDropdown} from 'platform/components';

import {useCallback} from 'react';

import {RowContextMenuContent} from '../components/RowContextMenu/RowContextMenuContent';
import type {EagGridStore} from '../store/createEagGridStore';
import type {ActionDefinitionObject} from '../types/ActionDefinition';
import type {EagColumn} from '../types/EagColumn';
import type {RowData} from '../types/RowData';
import type {ClipboardCopyMode} from '../utils/buildClipboardTsv';
import {buildFilterByValueAction, buildFilterByValueLabel} from '../utils/buildFilterByValueAction';
import {copyRowsToClipboard} from '../utils/copyRowsToClipboard';
import {getRowContextMenuSections} from '../utils/getRowContextMenuSections';
import {resolveClipboardCopyTarget} from '../utils/resolveClipboardCopyTarget';
import {useLatestRef} from './useLatestRef';

export type UseRowContextMenuParams = {
  store: EagGridStore;
  columns: EagColumn[];
  rows: RowData[];
  actionsDefinition: ActionDefinitionObject | null;
  clipboardEnabled: boolean;
  /** Gates the "Filter by value" item (EAG_GRID_EXPERIMENTAL_FILTER_BY_VALUE flag via EagGrid prop). */
  filterByValueEnabled: boolean;
  onActionClick: (actionKey: string, row: RowData, event: React.MouseEvent) => void;
  onFilterChange: (key: string, value: unknown) => void;
};

export function useRowContextMenu(
  params: UseRowContextMenuParams
): (row: RowData, event: React.MouseEvent) => void {
  // Stable callback that always reads the latest params (rows/selection change frequently).
  const paramsRef = useLatestRef(params);

  // Referential stability is load-bearing, not a premature optimization: this callback is
  // passed as `onRowContextMenu` to the memo()-wrapped GridRow (rendered once per row). A new
  // identity each render would defeat that memo for every row on any parent re-render. eag-grid
  // is on the React Compiler omit-list (apps/dms/rspack.config.ts), so the compiler never
  // supplies this stability — the manual useCallback + useLatestRef pattern must.
  return useCallback(
    (row: RowData, event: React.MouseEvent) => {
      const {
        store,
        columns,
        rows,
        actionsDefinition,
        clipboardEnabled,
        filterByValueEnabled,
        onActionClick,
        onFilterChange,
      } = paramsRef.current;

      event.preventDefault();

      const sections = actionsDefinition
        ? getRowContextMenuSections(row.actions, actionsDefinition)
        : [];

      const clickedCell =
        event.target instanceof Element ? event.target.closest('td[data-column]') : null;
      const rowIndexAttr = clickedCell?.closest('tr[aria-rowindex]')?.getAttribute('aria-rowindex');
      const clickedRowIndex =
        rowIndexAttr != null && !Number.isNaN(Number(rowIndexAttr)) ? Number(rowIndexAttr) : null;

      const clickedColumnKey = clickedCell?.getAttribute('data-column') ?? null;
      const clickedColumn = columns.find((column) => column.key === clickedColumnKey);
      const filterByValueAction =
        clickedColumn && filterByValueEnabled
          ? buildFilterByValueAction(
              clickedColumn,
              row[clickedColumn.field],
              store.getState().filterValues[clickedColumn.key]
            )
          : null;
      const onFilterByValue =
        clickedColumn && filterByValueAction
          ? () => onFilterChange(clickedColumn.key, filterByValueAction.payload)
          : null;
      const filterByValueLabel =
        onFilterByValue && clickedColumn ? buildFilterByValueLabel(row[clickedColumn.field]) : null;

      if (sections.length === 0 && !clipboardEnabled && onFilterByValue == null) {
        return;
      }

      const copyTarget = resolveClipboardCopyTarget({
        clickedRow: row,
        rows,
        columns,
        cellRange: store.getState().selectedCellRange,
        clickedColumnKey,
        clickedRowIndex,
      });

      openDropdown(
        event,
        <RowContextMenuContent
          sections={sections}
          clipboardEnabled={clipboardEnabled}
          onActionClick={(actionKey, actionEvent) => onActionClick(actionKey, row, actionEvent)}
          onCopy={(mode: ClipboardCopyMode) => {
            void copyRowsToClipboard(copyTarget.rows, copyTarget.columns, mode);
          }}
          onFilterByValue={onFilterByValue}
          filterByValueLabel={filterByValueLabel}
          filterByValueMode={filterByValueAction?.mode}
        />
      );
    },
    [paramsRef]
  );
}
